import { useMemo, useState } from "react";
import { Search, Star, CalendarCheck, Download, X, Clock, MapPin, IndianRupee, SlidersHorizontal, FileSpreadsheet, Timer } from "lucide-react";

// ---------------------------------------------------------------------------
// Sample data — replace this with your API / DB data. Shape stays the same.
// ---------------------------------------------------------------------------
const DEFAULT_MENTORS = [
    {
        id: "m1",
        name: "Dr. Ananya Reddy",
        initials: "DA",
        expertise: "Additive Manufacturing",
        institution: "IIT Tirupati",
        category: "Manufacturing",
        rating: 4.9,
        price: 1500,
        duration: "60 min",
        timings: "Mon & Wed, 6:00 PM - 7:00 PM",
        experience: "12 yrs",
        location: "Tirupati, AP",
    },
    {
        id: "m2",
        name: "Prof. Karthik Iyer",
        initials: "PK",
        expertise: "Robotics & Mechatronics",
        institution: "IIIT Sri City",
        category: "Robotics",
        rating: 4.8,
        price: 1800,
        duration: "45 min",
        timings: "Tue & Thu, 5:30 PM - 6:15 PM",
        experience: "15 yrs",
        location: "Sri City, AP",
    },
    {
        id: "m3",
        name: "Meera Sundaram",
        initials: "MS",
        expertise: "PCB & Embedded Design",
        institution: "APIC Visakhapatnam",
        category: "Electronics",
        rating: 4.7,
        price: 1200,
        duration: "45 min",
        timings: "Sat, 10:00 AM - 10:45 AM",
        experience: "8 yrs",
        location: "Visakhapatnam, AP",
    },
    {
        id: "m4",
        name: "Rahul Varma",
        initials: "RV",
        expertise: "EV Powertrain",
        institution: "Ashok Leyland R&D",
        category: "Automotive",
        rating: 4.9,
        price: 2200,
        duration: "60 min",
        timings: "Fri, 4:00 PM - 5:00 PM",
        experience: "10 yrs",
        location: "Chennai, TN",
    },
];

const BASE_CATEGORIES = ["Manufacturing", "Robotics", "Electronics", "Automotive"];

// ---------------------------------------------------------------------------
// CSV export helper
// ---------------------------------------------------------------------------
function exportToCSV(rows) {
    const headers = [
        "Name",
        "Expertise",
        "Institution",
        "Category",
        "Timings",
        "Duration",
        "Rating",
        "Price (INR)",
        "Experience",
        "Location",
    ];
    const lines = rows.map((r) =>
        [r.name, r.expertise, r.institution, r.category, r.timings, r.duration, r.rating, r.price, r.experience, r.location]
            .map((v) => `"${String(v).replace(/"/g, '""')}"`)
            .join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `mentor-directory-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Booking popup
// ---------------------------------------------------------------------------
const SLOTS = ["10:00 AM", "11:30 AM", "2:00 PM", "4:00 PM", "6:00 PM"];

function BookingModal({ mentor, onClose }) {
    const [date, setDate] = useState("");
    const [slot, setSlot] = useState(SLOTS[0]);
    const [confirmed, setConfirmed] = useState(false);

    if (!mentor) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="h-12 w-12 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-semibold">
                            {mentor.initials}
                        </div>
                        <div>
                            <p className="font-semibold text-slate-900 leading-tight">{mentor.name}</p>
                            <p className="text-sm text-slate-500">{mentor.expertise}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                {confirmed ? (
                    <div className="p-6 text-center space-y-2">
                        <CalendarCheck className="h-10 w-10 text-emerald-500 mx-auto" />
                        <p className="font-semibold text-slate-900">Session booked</p>
                        <p className="text-sm text-slate-500">
                            {mentor.name} · {date || "date TBD"} · {slot}
                        </p>
                        <button
                            onClick={onClose}
                            className="mt-3 w-full rounded-lg bg-slate-900 text-white py-2 text-sm font-medium hover:bg-slate-800"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <div className="p-5 space-y-4">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-slate-400 text-xs">Institution</p>
                                <p className="text-slate-700 font-medium">{mentor.institution}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-slate-400 text-xs">Rating</p>
                                <p className="text-slate-700 font-medium flex items-center gap-1">
                                    <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {mentor.rating}
                                </p>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Pick a date</label>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Pick a time slot</label>
                            <div className="mt-1 flex flex-wrap gap-2">
                                {SLOTS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setSlot(s)}
                                        className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${slot === s
                                            ? "bg-indigo-600 border-indigo-600 text-white"
                                            : "border-slate-200 text-slate-600 hover:border-indigo-300"
                                            }`}
                                    >
                                        <Clock className="h-3 w-3" />
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center justify-between rounded-lg bg-indigo-50 px-3 py-2">
                            <span className="text-sm text-slate-600">Session fee</span>
                            <span className="flex items-center font-semibold text-indigo-700">
                                <IndianRupee className="h-3.5 w-3.5" />
                                {mentor.price.toLocaleString("en-IN")}
                            </span>
                        </div>

                        <button
                            disabled={!date}
                            onClick={() => setConfirmed(true)}
                            className="w-full rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-2.5 text-sm font-medium shadow-sm hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                        >
                            Confirm booking
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Export popup — previews every course/session detail in a table,
// then lets the user confirm the download.
// ---------------------------------------------------------------------------
function ExportModal({ rows, onClose }) {
    const [downloaded, setDownloaded] = useState(false);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-4xl max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-100 p-5">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
                            <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                            <p className="font-semibold text-slate-900">Export course details</p>
                            <p className="text-xs text-slate-500">{rows.length} course{rows.length !== 1 ? "s" : ""} ready to export</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" aria-label="Close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Table preview */}
                <div className="overflow-auto flex-1">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50/95 backdrop-blur">
                            <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                <th className="px-4 py-3">Course / Mentor</th>
                                <th className="px-4 py-3">Timings</th>
                                <th className="px-4 py-3">Duration</th>
                                <th className="px-4 py-3">Rating</th>
                                <th className="px-4 py-3">Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-slate-50 last:border-0">
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-slate-900">{r.name}</p>
                                        <p className="text-xs text-slate-500">{r.expertise} · {r.institution}</p>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">
                                        <span className="flex items-center gap-1.5">
                                            <Clock className="h-3.5 w-3.5 text-slate-400" />
                                            {r.timings}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">
                                        <span className="flex items-center gap-1.5">
                                            <Timer className="h-3.5 w-3.5 text-slate-400" />
                                            {r.duration}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="flex items-center gap-1 font-medium text-slate-700">
                                            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {r.rating}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="flex items-center font-semibold text-indigo-600">
                                            <IndianRupee className="h-3.5 w-3.5" />
                                            {r.price.toLocaleString("en-IN")}
                                        </span>
                                    </td>
                                </tr>
                            ))}

                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">
                                        No courses to export — adjust your filters first.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-slate-100 p-4">
                    <p className="text-xs text-slate-400">Exports the courses currently shown, exactly as previewed above.</p>
                    <button
                        disabled={rows.length === 0}
                        onClick={() => {
                            exportToCSV(rows);
                            setDownloaded(true);
                        }}
                        className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                    >
                        <Download className="h-4 w-4" />
                        {downloaded ? "Downloaded — export again" : "Download CSV"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Add-course popup — mentor/admin fills in every detail here; on submit
// it's appended and shows up immediately in the table above.
// ---------------------------------------------------------------------------
const emptyForm = {
    name: "",
    expertise: "",
    institution: "",
    category: "",
    timings: "",
    duration: "",
    price: "",
    rating: "",
    experience: "",
    location: "",
};

function getInitials(name) {
    const parts = name
        .replace(/^(Dr\.|Prof\.|Mr\.|Ms\.|Mrs\.)\s*/i, "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (parts.length === 0) return "??";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function AddCourseModal({ categories, onClose, onSave }) {
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState("");

    const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.expertise || !form.price || !form.timings) {
            setError("Name, expertise, timings and price are required.");
            return;
        }
        onSave({
            id: `m${Date.now()}`,
            initials: getInitials(form.name),
            name: form.name,
            expertise: form.expertise,
            institution: form.institution || "—",
            category: form.category || "General",
            timings: form.timings,
            duration: form.duration || "—",
            price: Number(form.price) || 0,
            rating: form.rating ? Number(form.rating) : 0,
            experience: form.experience || "—",
            location: form.location || "—",
        });
        setForm(emptyForm);
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <form
                onSubmit={handleSubmit}
                className="w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-100 p-5">
                    <div>
                        <p className="font-semibold text-slate-900">Add a course</p>
                        <p className="text-xs text-slate-500">Fill in the details — it'll appear in the table right away.</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Form fields */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {error && (
                        <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-600">{error}</p>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <label className="text-xs font-medium text-slate-500">Mentor / instructor name *</label>
                            <input
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Dr. Ananya Reddy"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="text-xs font-medium text-slate-500">Course / expertise *</label>
                            <input
                                value={form.expertise}
                                onChange={update("expertise")}
                                placeholder="Additive Manufacturing"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Institution</label>
                            <input
                                value={form.institution}
                                onChange={update("institution")}
                                placeholder="IIT Tirupati"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Category</label>
                            <input
                                list="category-options"
                                value={form.category}
                                onChange={update("category")}
                                placeholder="Manufacturing"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                            <datalist id="category-options">
                                {categories.map((c) => (
                                    <option key={c} value={c} />
                                ))}
                            </datalist>
                        </div>

                        <div className="col-span-2">
                            <label className="text-xs font-medium text-slate-500">Timings *</label>
                            <input
                                value={form.timings}
                                onChange={update("timings")}
                                placeholder="Mon & Wed, 6:00 PM - 7:00 PM"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Session duration</label>
                            <input
                                value={form.duration}
                                onChange={update("duration")}
                                placeholder="60 min"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Price (INR) *</label>
                            <input
                                type="number"
                                min="0"
                                value={form.price}
                                onChange={update("price")}
                                placeholder="1500"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Rating (0-5)</label>
                            <input
                                type="number"
                                min="0"
                                max="5"
                                step="0.1"
                                value={form.rating}
                                onChange={update("rating")}
                                placeholder="4.8"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-medium text-slate-500">Experience</label>
                            <input
                                value={form.experience}
                                onChange={update("experience")}
                                placeholder="12 yrs"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="text-xs font-medium text-slate-500">Location</label>
                            <input
                                value={form.location}
                                onChange={update("location")}
                                placeholder="Tirupati, AP"
                                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:opacity-95 transition-opacity"
                    >
                        <CalendarCheck className="h-4 w-4" />
                        Save course
                    </button>
                </div>
            </form>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function MentorDirectory() {
    const [mentors, setMentors] = useState(DEFAULT_MENTORS);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("All");
    const [minRating, setMinRating] = useState(0);
    const [maxPrice, setMaxPrice] = useState(3000);
    const [sortKey, setSortKey] = useState("rating");
    const [activeMentor, setActiveMentor] = useState(null);
    const [exportOpen, setExportOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);

    const categories = useMemo(
        () => Array.from(new Set([...BASE_CATEGORIES, ...mentors.map((m) => m.category)])),
        [mentors]
    );

    const filtered = useMemo(() => {
        let rows = mentors.filter((m) => {
            const matchesSearch =
                m.name.toLowerCase().includes(search.toLowerCase()) ||
                m.expertise.toLowerCase().includes(search.toLowerCase()) ||
                m.institution.toLowerCase().includes(search.toLowerCase());
            const matchesCategory = category === "All" || m.category === category;
            const matchesRating = m.rating >= minRating;
            const matchesPrice = m.price <= maxPrice;
            return matchesSearch && matchesCategory && matchesRating && matchesPrice;
        });

        rows = rows.sort((a, b) => {
            if (sortKey === "rating") return b.rating - a.rating;
            if (sortKey === "price_low") return a.price - b.price;
            if (sortKey === "price_high") return b.price - a.price;
            return a.name.localeCompare(b.name);
        });

        return rows;
    }, [mentors, search, category, minRating, maxPrice, sortKey]);

    return (
        <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
            <div className="mx-auto max-w-6xl">
                {/* Header */}
                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">Mentor Directory</h1>
                        <p className="text-sm text-slate-500">
                            {filtered.length} of {mentors.length} mentors match your filters
                        </p>
                    </div>
                    <div className="flex gap-2 self-start">
                        <button
                            onClick={() => setAddOpen(true)}
                            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 py-2 text-sm font-medium shadow-sm hover:opacity-95 transition-opacity"
                        >
                            <CalendarCheck className="h-4 w-4" />
                            Add course
                        </button>
                        <button
                            onClick={() => setExportOpen(true)}
                            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 transition-colors"
                        >
                            <Download className="h-4 w-4" />
                            Export
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-6 grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="relative lg:col-span-2">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, expertise, institution..."
                            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                    </div>

                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        {["All", ...categories].map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>

                    <select
                        value={minRating}
                        onChange={(e) => setMinRating(Number(e.target.value))}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option value={0}>Any rating</option>
                        <option value={4.5}>4.5+ stars</option>
                        <option value={4.8}>4.8+ stars</option>
                    </select>

                    <select
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value)}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option value="rating">Sort: Rating (high-low)</option>
                        <option value="price_low">Sort: Price (low-high)</option>
                        <option value="price_high">Sort: Price (high-low)</option>
                        <option value="name">Sort: Name (A-Z)</option>
                    </select>

                    <div className="flex items-center gap-2 lg:col-span-5">
                        <SlidersHorizontal className="h-4 w-4 text-slate-400 shrink-0" />
                        <input
                            type="range"
                            min={500}
                            max={3000}
                            step={100}
                            value={maxPrice}
                            onChange={(e) => setMaxPrice(Number(e.target.value))}
                            className="w-full accent-indigo-600"
                        />
                        <span className="whitespace-nowrap text-xs font-medium text-slate-500">
                            Up to ₹{maxPrice.toLocaleString("en-IN")}
                        </span>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                    <th className="px-4 py-3">Mentor</th>
                                    <th className="px-4 py-3">Expertise</th>
                                    <th className="px-4 py-3">Institution</th>
                                    <th className="px-4 py-3">Category</th>
                                    <th className="px-4 py-3">Timings</th>
                                    <th className="px-4 py-3">Rating</th>
                                    <th className="px-4 py-3">Price</th>
                                    {/* <th className="px-4 py-3 text-right">Action</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((m) => (
                                    <tr key={m.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-xs font-semibold text-white">
                                                    {m.initials}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900">{m.name}</p>
                                                    <p className="flex items-center gap-1 text-xs text-slate-400">
                                                        <MapPin className="h-3 w-3" /> {m.location}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">{m.expertise}</td>
                                        <td className="px-4 py-3 text-slate-600">{m.institution}</td>
                                        <td className="px-4 py-3">
                                            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700">
                                                {m.category}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-500">
                                            <span className="flex items-center gap-1.5">
                                                <Clock className="h-3.5 w-3.5 text-slate-400" />
                                                {m.timings}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="flex items-center gap-1 font-medium text-slate-700">
                                                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {m.rating}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="flex items-center font-semibold text-indigo-600">
                                                <IndianRupee className="h-3.5 w-3.5" />
                                                {m.price.toLocaleString("en-IN")}
                                                <span className="ml-1 font-normal text-slate-400">/session</span>
                                            </span>
                                        </td>
                                        {/* <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => setActiveMentor(m)}
                                                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:opacity-95 transition-opacity"
                                            >
                                                <CalendarCheck className="h-3.5 w-3.5" />
                                                Book
                                            </button>
                                        </td> */}
                                    </tr>
                                ))}

                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">
                                            No mentors match these filters. Try widening your search.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <BookingModal mentor={activeMentor} onClose={() => setActiveMentor(null)} />
            {exportOpen && <ExportModal rows={filtered} onClose={() => setExportOpen(false)} />}
            {addOpen && (
                <AddCourseModal
                    categories={categories}
                    onClose={() => setAddOpen(false)}
                    onSave={(course) => setMentors((prev) => [course, ...prev])}
                />
            )}
        </div>
    );
}